import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useMotionValue, useTransform } from "framer-motion";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Heart, X, Globe, Layers, RotateCcw, Lock, TrendingUp, Crown, Sparkles } from "lucide-react";
import FoundingBadge from "../components/FoundingBadge";
import { ReputationBadge } from "../components/ReputationBadge";

function fmtTraffic(n) {
  if (n == null) return "—";
  if (n >= 1000) return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}K`;
  return `${n}`;
}

export default function Feed() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [cards, setCards] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/feed");
      setCards(data);
    } catch (err) {
      if (err.response?.status === 400 || err.response?.status === 403) {
        toast.error(formatApiError(err.response?.data?.detail));
        nav("/dashboard");
        return;
      }
      toast.error(formatApiError(err.response?.data?.detail));
      setCards([]);
    }
  };

  useEffect(() => {
    if (user && (!user.site || !user.site.owner_verified)) { nav("/dashboard"); return; }
    load();
    // eslint-disable-next-line
  }, []);

  const top = cards && cards.length ? cards[cards.length - 1] : null;

  const act = async (action) => {
    if (!top || busy) return;
    setBusy(true);
    const target = top;
    const wasLast = cards.length <= 1;
    setCards((c) => c.slice(0, -1));
    try {
      const { data } = await api.post("/swipe", { target_user_id: target.user_id, action });
      if (data.matched) {
        toast.success("It's a match! 🎉 Open it to generate your guest post.", {
          action: { label: "Open", onClick: () => nav(`/matches/${data.match_id}`) },
        });
        window.dispatchEvent(new Event("blinxd:notify"));
      } else if (data.daily_match_limit) {
        toast.info("Mutual like! You've hit today's match limit — it'll surface in your matches soon. Upgrade for more matches per day.", {
          duration: 7000, action: { label: "Upgrade", onClick: () => nav("/pricing") },
        });
      } else if (target.beyond_plan && action === "like") {
        toast.info("Nice pick! This site is above your plan's traffic tier. Upgrade to match sites like this every day.", {
          duration: 7000, action: { label: "Upgrade", onClick: () => nav("/pricing") },
        });
      }
      // Deck exhausted — pull the next cycle so passed-but-not-matched sites get another chance.
      if (wasLast) await load();
    } catch (err) {
      // Cap reached (409) or plan limit (402): restore the card so the user can retry.
      if (err.response?.status === 409 || err.response?.status === 402) {
        setCards((c) => [...c, target]);
        const opts = { duration: 7000 };
        if (err.response?.status === 402) opts.action = { label: "Upgrade", onClick: () => nav("/pricing") };
        toast.error(formatApiError(err.response?.data?.detail), opts);
      } else {
        toast.error(formatApiError(err.response?.data?.detail));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto px-6 py-10">
      <div className="text-center">
        <div className="font-mono text-[11px] tracking-widest text-muted-foreground">THE POOL</div>
        <h1 className="font-serif font-bold text-4xl tracking-tight mt-2">Discover swaps</h1>
        <p className="text-muted-foreground text-sm mt-2 font-mono">
          {user?.site?.niche} · DA {user?.site?.da} · ±10 band{cards ? ` · ${cards.length} left` : ""}
        </p>
        {user?.limits?.swipes_per_day != null && (
          <button onClick={() => nav("/pricing")} data-testid="feed-upgrade-nudge"
            className="mt-3 inline-flex items-center gap-1.5 text-xs font-mono text-primary hover:underline">
            <Sparkles size={12} />
            {Math.max(0, user.limits.swipes_per_day - (user.usage?.swipes || 0)) + (user.bonus_swipes || 0)} swipes left today
            {user.bonus_swipes ? ` (incl. ${user.bonus_swipes} bonus)` : ""} · Upgrade for more
          </button>
        )}
      </div>

      <div className="relative mt-10 h-[540px]">
        {cards === null && (
          <div className="absolute inset-0 flex items-center justify-center font-mono text-sm text-muted-foreground animate-pulse">loading pool…</div>
        )}
        {cards && cards.length === 0 && (
          <div className="absolute inset-0 flex flex-col items-center justify-center rounded-2xl border border-white/10 bg-zinc-900/60 text-center p-8 shadow-soft mesh-primary" data-testid="feed-empty-state">
            <div className="h-14 w-14 rounded-2xl bg-primary/15 border border-primary/25 flex items-center justify-center glow-primary">
              <Sparkles size={26} className="text-primary" />
            </div>
            <h3 className="font-serif font-semibold text-2xl mt-5">The pool's still filling up 🚀</h3>
            <p className="text-muted-foreground text-sm mt-3 max-w-sm leading-relaxed">
              You're early. New sites in your
              niche and DA band are rolling in fast. Give it a beat, hit refresh, and fresh faces to swipe on will appear.
            </p>
            <p className="text-muted-foreground/80 text-[13px] mt-3 max-w-sm">
              Endless swiping is in your future — we promise. 💫 Thanks for helping build the pool.
            </p>
            <Button onClick={load} variant="outline" data-testid="reload-feed" className="mt-6 rounded-full border-white/15 bg-white/5 hover:bg-white/10 text-foreground">
              <RotateCcw size={16} className="mr-2" /> Check for new sites
            </Button>
          </div>
        )}
        <AnimatePresence>
          {cards && cards.map((card, i) => (
            <SwipeCard key={card.user_id} card={card} isTop={i === cards.length - 1} depth={cards.length - 1 - i} onSwipe={act} />
          ))}
        </AnimatePresence>
      </div>

      {top && (
        <div className="mt-8 flex items-center justify-center gap-6">
          <button onClick={() => act("pass")} disabled={busy} data-testid="swipe-pass-button"
            className="h-16 w-16 rounded-full bg-white border border-stone-200 shadow-soft flex items-center justify-center text-slate-pass hover:scale-105 active:scale-95 transition-transform">
            <X size={26} />
          </button>
          <button onClick={() => act("like")} disabled={busy} data-testid="swipe-like-button"
            className="h-20 w-20 rounded-full bg-terracotta text-white shadow-soft flex items-center justify-center hover:scale-105 active:scale-95 transition-transform">
            <Heart size={30} fill="currentColor" />
          </button>
        </div>
      )}
    </div>
  );
}

function SwipeCard({ card, isTop, depth, onSwipe }) {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-12, 12]);
  const likeOpacity = useTransform(x, [40, 150], [0, 1]);
  const passOpacity = useTransform(x, [-40, -150], [0, 1]);

  return (
    <motion.div
      data-testid={isTop ? "swipe-card-top" : undefined}
      className="absolute inset-x-0 mx-auto max-w-md select-none"
      style={{ x: isTop ? x : 0, rotate: isTop ? rotate : 0, zIndex: 10 - depth, top: depth * 10, scale: 1 - depth * 0.04 }}
      drag={isTop ? "x" : false}
      dragConstraints={{ left: 0, right: 0 }}
      onDragEnd={(_, info) => {
        if (info.offset.x > 130) onSwipe("like");
        else if (info.offset.x < -130) onSwipe("pass");
      }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ x: x.get() >= 0 ? 420 : -420, opacity: 0, transition: { duration: 0.25 } }}
    >
      <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden shadow-card">
        <div className="relative h-64">
          <img src={card.image} alt={card.name} className="w-full h-full object-cover pointer-events-none"
            onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=940&q=80"; }} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
          <div className="absolute top-4 left-4 bg-zinc-950/80 backdrop-blur px-3 py-1.5 rounded-full font-mono text-sm font-bold text-primary border border-white/10">DA {card.da}</div>
          <div className="absolute top-4 right-4 flex flex-col items-end gap-2">
            <span className="bg-emerald-500 text-white px-3 py-1.5 rounded-full text-xs font-medium">{card.niche}</span>
            {card.high_traffic && (
              <span className="bg-ink text-white px-2.5 py-1 rounded-full text-[10px] font-mono tracking-wide flex items-center gap-1" data-testid="high-traffic-badge">
                <Crown size={11} /> HIGH TRAFFIC
              </span>
            )}
            {card.beyond_plan && (
              <span className="bg-amber-500/90 text-white px-2.5 py-1 rounded-full text-[10px] font-mono tracking-wide flex items-center gap-1" data-testid="beyond-plan-badge">
                <Crown size={11} /> UPGRADE PICK
              </span>
            )}
            {card.founding_member && <FoundingBadge size={14} label />}
            {card.reputation && <ReputationBadge rep={card.reputation} />}
          </div>
          <div className="absolute bottom-4 left-5 right-5 text-white">
            <div className="flex items-center gap-1.5 text-white/80"><Lock size={12} /><span className="font-mono text-[11px]">Identity hidden until you match & verify</span></div>
            <h3 className="font-serif font-bold text-3xl tracking-tight mt-1 drop-shadow">{card.niche} site</h3>
          </div>
          <motion.div style={{ opacity: likeOpacity }}
            className="absolute top-6 left-6 border-2 border-terracotta text-terracotta bg-white/90 px-4 py-1.5 rounded-lg font-serif font-bold text-2xl -rotate-12">LIKE</motion.div>
          <motion.div style={{ opacity: passOpacity }}
            className="absolute top-6 right-6 border-2 border-slate-pass text-slate-pass bg-white/90 px-4 py-1.5 rounded-lg font-serif font-bold text-2xl rotate-12">PASS</motion.div>
        </div>
        <div className="p-6">
          <div className="flex items-center gap-6 mb-4">
            <div>
              <div className="font-mono text-[10px] tracking-widest text-muted-foreground">DOMAIN AUTHORITY</div>
              <div className="font-serif font-bold text-2xl mt-0.5">{card.da}</div>
            </div>
            <div className="h-8 w-px bg-border" />
            <div>
              <div className="font-mono text-[10px] tracking-widest text-muted-foreground flex items-center gap-1"><TrendingUp size={11} /> MONTHLY VISITS</div>
              <div className="font-serif font-bold text-2xl mt-0.5" data-testid="card-traffic">
                {fmtTraffic(card.traffic)}<span className="text-muted-foreground text-xs font-sans font-normal">/mo · est.</span>
              </div>
            </div>
          </div>
          <p className="text-muted-foreground leading-relaxed text-[15px]">{card.description}</p>
        </div>
      </div>
    </motion.div>
  );
}
